import React, { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "./AreaChart.css";

export default function AreaChart({
  dataPoints = [],
  width = 550,
  height = 400,
}) {
  const { isDateDataset, categories, seriesData, tickPositions } = useMemo(() => {
    const pts = dataPoints || [];

    const isDateLike = (v) => {
      if (v instanceof Date) return true;
      if (typeof v === "string") return !Number.isNaN(Date.parse(v));
      if (typeof v === "number") return v >= 1e9;
      return false;
    };

    const allDate = pts.length > 0 && pts.every(d => isDateLike(d?.x ?? d?.label));

    if (allDate) {
      const toTime = (v) => {
        if (v instanceof Date) return v.getTime();
        if (typeof v === "string") {
          const t = Date.parse(v);
          return Number.isNaN(t) ? null : t;
        }
        if (typeof v === "number") {
          return v < 1e12 ? v * 1000 : v;
        }
        return null;
      };

      const daySum = new Map();
      pts.forEach((d) => {
        const rawX = d?.x ?? d?.label;
        const t = toTime(rawX);
        if (t == null) return;
        const day = new Date(t).getDate(); 
        const y = Number(d?.y ?? 0);
        daySum.set(day, (daySum.get(day) ?? 0) + y);
      });

      const cats = Array.from(daySum.keys()).sort((a, b) => a - b);
      const ys = cats.map((day) => daySum.get(day));
      const ticks = cats.map((_, i) => i);

      return {
        isDateDataset: true,
        categories: cats,
        seriesData: ys,
        tickPositions: ticks,
      };
    }

    const cats = pts.map((d) => String(d?.label ?? d?.x ?? ""));
    const ys = pts.map((d) => Number(d?.y ?? 0));
    const ticks = cats.map((_, i) => i);

    return {
      isDateDataset: false,
      categories: cats,
      seriesData: ys,
      tickPositions: ticks,
    };
  }, [dataPoints]);

  const fewPoints = seriesData.length <= 2;

  const options = {
    chart: {
      type: "area",
      backgroundColor: "transparent",
      width,
      height,
      animation: false,
    },
    title: { text: null },
    credits: { enabled: false },
    legend: { enabled: false },
    tooltip: { enabled: false },

    xAxis: {
      type: "category",
      categories,
      tickPositions, 
      labels: {
        step: 1,
        style: { fontSize: "9px", color: "#666" },
        formatter() {
          return this.value;
        },
      },
      tickmarkPlacement: "on",
      lineColor: "#ccc",
      tickLength: 0,
    },

    yAxis: {
      title: { text: null },
      labels: { enabled: false },
      gridLineWidth: 0,
      lineWidth: 0,
      tickLength: 0,
    },

    plotOptions: {
      area: {
        fillOpacity: 0.45,
        marker: { enabled: fewPoints },
        dataLabels: {
          enabled: true,
          style: { fontSize: "10px", color: "red", textOutline: "none" },
          formatter() { return this.y; },
        },
        states: { hover: { halo: { size: 0 } } },
      },
      series: { animation: false, lineWidth: 2, color: "#5a88c7" },
    },

    series: [{ name: isDateDataset ? "Notícias (por dia)" : "Série", data: seriesData }],
  };

  return (
  <div style={{ marginTop: 50 }}>
    <HighchartsReact highcharts={Highcharts} options={options} />
  </div>
);
}
